import { cn } from "../../../../utils/cn.ts";
import { FontStyles } from "../../../../utils/font-styles.ts";
import { focusStyles, focusStylesInsetThin } from "../../../../utils/focus-styles.ts";
import { GalleryActionButton } from "./gallery-action-button.tsx";
import "../../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../../assets/fa-icons/css/sharp-regular.css";
import { usePropertyDetailsContext } from "../../../contexts/property-details-context.tsx";
import { BookingButton } from "../../../components/booking-button.tsx";
import { Sharing, type ShareMethod } from "../../../../components/sharing.tsx";
import { AgentLogoImage } from "../../../../components/agent-logo-image.tsx";
import { useState } from "react";

export interface AgentContactBarProps {
  onShare?: (method: ShareMethod) => void;
  onWatchClick?: () => void;
  isWatchlisted?: boolean;
  watchlistLoading?: boolean;
  watchlistAriaLabel: string;
  sharePropertyAriaLabel: string;
}

export const AgentContactBar = ({
  onShare,
  onWatchClick,
  isWatchlisted = false,
  watchlistLoading = false,
  watchlistAriaLabel,
  sharePropertyAriaLabel,
}: AgentContactBarProps) => {
  const { agentLogoUrl, agentName, agentPhone, agentPageUrl, shareUrl } = usePropertyDetailsContext();
  const [isShareExpanded, setIsShareExpanded] = useState(false);

  return (
    <div className="bg-neutral-white z-[999] border-t border-foggy-firth flex shadow-[0px_0px_15px_0px_rgba(0,0,0,0.1)] lg:hidden">
      {agentLogoUrl && (
        <div className="self-stretch md:self-auto border-r border-r-foggy-firth flex items-center justify-center w-[36%] h-[124px] md:w-[25%] md:h-[111px] p-3.5 overflow-hidden">
          <AgentLogoImage
            agentLogoUrl={agentLogoUrl}
            agentName={agentName}
            agentPageUrl={agentPageUrl}
            imgClassName="max-w-full max-h-full object-contain"
          />
        </div>
      )}

      <div className="flex flex-col gap-3 p-3.5 md:p-5 md:gap-3.5 flex-1 items-center md:items-stretch">
        <div className="w-full flex flex-col-reverse items-center gap-3 md:flex-row md:justify-between md:gap-0 lg:justify-center">
          {agentPhone && (
            <>
              <a
                href={`tel:${agentPhone}`}
                aria-label={`Call agent on ${agentPhone}`}
                className={cn(
                  FontStyles["body-md"],
                  focusStylesInsetThin,
                  "lg:hidden",
                  "tracking-normal text-slate !leading-none text-center md:text-left",
                )}
              >
                Call agent: <span className="font-semibold !no-underline !text-inherit">{agentPhone}</span>
              </a>
              <p
                aria-hidden="true"
                className={cn(
                  FontStyles["body-md"],
                  "hidden lg:block",
                  "tracking-normal text-slate !leading-none  text-center",
                )}
              >
                Call agent: <span className="font-semibold !no-underline !text-inherit">{agentPhone}</span>
              </p>
            </>
          )}
          <div
            className={cn(
              "lg:hidden flex items-center",
              !agentPhone && "md:ml-auto",
              // Expanded: 10px between the social buttons on mobile, 12px from tablet up.
              isShareExpanded ? "gap-2.5 md:gap-3" : "gap-3.5",
            )}
          >
            <Sharing
              url={shareUrl}
              expandable
              isExpanded={isShareExpanded}
              onExpandedChange={setIsShareExpanded}
              onShare={onShare}
              nativeShareAriaLabel={sharePropertyAriaLabel}
              expandAriaLabel={sharePropertyAriaLabel}
              nativeShareLabel={<span className="font-medium text-slate">Share</span>}
              className={cn(
                FontStyles["body-md"],
                "shadow-loch-800 ",
                focusStyles,
                "lg:active:!bg-loch-100",
                "lg:text-body-l flex gap-1.5 md:gap-2 lg:py-4.5 lg:px-1.5 lg:hover:bg-loch-50 items-center cursor-pointer !leading-none",
              )}
              iconClassName="text-loch"
            />
            <GalleryActionButton
              icon="heart"
              ariaLabel={watchlistAriaLabel}
              ariaPressed={isWatchlisted}
              label={isWatchlisted ? "Saved" : "Save"}
              isSolidIcon={isWatchlisted}
              loading={watchlistLoading}
              onClick={onWatchClick}
            />
          </div>
        </div>

        <BookingButton className="w-full max-xss:text-body-xs" />
      </div>
    </div>
  );
};
